/** Minimal JSX-to-DOM factory — the whole "framework".
 *
 * esbuild compiles JSX to `h(...)` calls (see ui/tsconfig.json); h() builds
 * real DOM nodes once, at evaluation time. No virtual DOM, no diffing, no
 * runtime dependency — the sign-in flow is a step machine over static markup,
 * so imperative show/hide on held element references is all it needs. If this
 * UI ever needs stateful re-rendering, swap the factory for Preact by
 * repointing jsxFactory and deleting this file.
 *
 * Conventions:
 *   - `on*` function props attach listeners (onSubmit → "submit");
 *   - `true` sets a bare attribute, `false`/null/undefined skips it;
 *   - everything else is setAttribute, so markup can't smuggle HTML in —
 *     strings always land as attribute values or text nodes, never innerHTML.
 */

const SVG_NS = "http://www.w3.org/2000/svg";
const SVG_TAGS = new Set(["svg", "path", "circle", "g", "rect", "line", "polyline", "polygon", "defs", "use"]);

export type Children = Node | string | number | boolean | null | undefined | Children[];

type Component = (props: Record<string, unknown>) => Element;

export function h(
  tag: string | Component,
  props: Record<string, unknown> | null,
  ...children: Children[]
): Element {
  if (typeof tag === "function") {
    return tag({ ...(props ?? {}), children });
  }

  const el = SVG_TAGS.has(tag)
    ? document.createElementNS(SVG_NS, tag)
    : document.createElement(tag);

  for (const [name, value] of Object.entries(props ?? {})) {
    if (value == null || value === false) continue;
    if (name.startsWith("on") && typeof value === "function") {
      el.addEventListener(name.slice(2).toLowerCase(), value as EventListener);
    } else if (value === true) {
      el.setAttribute(name, "");
    } else {
      el.setAttribute(name, String(value));
    }
  }

  append(el, children);
  return el;
}

function append(el: Element, child: Children): void {
  if (child == null || typeof child === "boolean") return;
  if (Array.isArray(child)) {
    for (const c of child) append(el, c);
    return;
  }
  el.append(child instanceof Node ? child : String(child));
}

// TypeScript resolves JSX types from the factory's own namespace (`h.JSX`)
// when jsxFactory is "h" — scoped here so no global JSX (e.g. a transitive
// @types/react) can interfere.
export declare namespace h {
  namespace JSX {
    type Element = globalThis.Element;
    // Permissive by design: attributes are plain strings/booleans/handlers
    // passed to setAttribute/addEventListener, not typed DOM properties.
    interface IntrinsicElements {
      [tag: string]: Record<string, unknown>;
    }
  }
}
